import {
  CalendarCheck,
  ChevronLeft,
  Globe,
  MapPin,
  Pencil,
  Swords,
  Tag,
} from "lucide-react";
import {
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { Button } from "@/components/ui/button";

import { CompetitorForm } from "./CompetitorForm";
import { CompetitorTimeline } from "./CompetitorTimeline";
import { LineBadge } from "./CompetitorsView";
import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  SectionLabel,
} from "./components";
import { formatShortDate, toIsoDay } from "./dates";
import { MiniMarkdown } from "./MiniMarkdown";
import { MARKET_BASE } from "./routes";
import { TrackingStatusMenu } from "./TrackingStatus";
import type { MarketCompetitor } from "./types";
import { hostOf, riseStyle } from "./util";

/**
 * A competitor: every field (editable in a ResponsiveDialog), the quick
 * tracking status menu and the timeline of news and team notes.
 */
export const CompetitorShow = () => {
  const translate = useTranslate();
  const { id = "" } = useParams();
  const [editing, setEditing] = useState(false);
  const {
    data: competitor,
    isPending,
    error,
  } = useGetOne<MarketCompetitor>("market_competitors", { id });

  const back = (
    <Link
      to={`${MARKET_BASE}/competidores`}
      className="liquid-glass glass-press inline-flex h-10 items-center gap-1 rounded-full pr-4 pl-2.5 text-sm font-semibold text-[color:var(--glass-foreground)] md:h-9"
    >
      <ChevronLeft aria-hidden className="size-5" />
      {translate("market.competitors.back")}
    </Link>
  );

  if (isPending) return <LoadingCards count={2} />;
  if (error || !competitor)
    return (
      <div className="space-y-4">
        {back}
        <EmptyState
          icon={Swords}
          title={translate("market.competitors.not_found")}
        />
      </div>
    );

  return (
    <section aria-labelledby="mi-competitor-title" className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        {back}
        <Button
          type="button"
          variant="ghost"
          onClick={() => setEditing(true)}
          className="liquid-glass glass-press h-10 rounded-full px-4 text-sm font-semibold text-[color:var(--glass-foreground)] md:h-9"
        >
          <Pencil aria-hidden className="size-4" />
          {translate("market.competitors.edit")}
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <CompetitorDetails competitor={competitor} />
        <div
          className="mi-card mi-rise min-w-0 p-4 md:p-5"
          style={riseStyle(1)}
        >
          <CompetitorTimeline competitorId={competitor.id} />
        </div>
      </div>
      <CompetitorForm
        open={editing}
        onOpenChange={setEditing}
        competitor={competitor}
      />
    </section>
  );
};

const CompetitorDetails = ({
  competitor,
}: {
  competitor: MarketCompetitor;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [update, { isPending }] = useUpdate<MarketCompetitor>();
  const notify = useNotify();
  const today = toIsoDay(new Date());

  const markReviewed = () =>
    update(
      "market_competitors",
      {
        id: competitor.id,
        data: { last_reviewed_at: today },
        previousData: competitor,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("market.competitors.updated", { type: "success" }),
        onError: () => notify("market.competitors.error", { type: "error" }),
      },
    );

  return (
    <article className="mi-card mi-rise flex min-w-0 flex-col gap-3.5 self-start p-4 md:p-5">
      <div className="space-y-2">
        <h2
          id="mi-competitor-title"
          className="text-xl font-semibold tracking-tight wrap-anywhere md:text-[1.375rem]"
        >
          {competitor.name}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <LineBadge line={competitor.line} />
          <TrackingStatusMenu competitor={competitor} />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          {competitor.website ? (
            <ExternalAnchor href={competitor.website}>
              <Globe aria-hidden className="mr-1 inline size-3.5" />
              {hostOf(competitor.website)}
            </ExternalAnchor>
          ) : null}
          {competitor.location ? (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin aria-hidden className="size-3.5 shrink-0" />
              <span className="sr-only">
                {translate("market.competitors.location")}:{" "}
              </span>
              <span className="truncate">{competitor.location}</span>
            </span>
          ) : null}
        </div>
      </div>

      {competitor.entry_price || competitor.pricing ? (
        <Section label={translate("market.competitors.entry_price")}>
          {competitor.entry_price ? (
            <p className="inline-flex items-center gap-1.5 text-[15px] font-semibold wrap-anywhere">
              <Tag aria-hidden className="size-4 text-primary" />
              {competitor.entry_price}
            </p>
          ) : null}
          {competitor.pricing ? (
            <p className="text-sm text-muted-foreground wrap-anywhere">
              {competitor.pricing}
            </p>
          ) : null}
        </Section>
      ) : null}
      {competitor.value_proposition ? (
        <Section label={translate("market.competitors.value_proposition")}>
          <p className="text-[15px] wrap-anywhere">
            {competitor.value_proposition}
          </p>
        </Section>
      ) : null}
      {competitor.description ? (
        <Section label={translate("market.competitors.what_they_do")}>
          <MiniMarkdown source={competitor.description} />
        </Section>
      ) : null}
      {competitor.channels ? (
        <Section label={translate("market.competitors.channels")}>
          <p className="text-sm wrap-anywhere">{competitor.channels}</p>
        </Section>
      ) : null}
      {competitor.strengths ? (
        <Section label={translate("market.competitors.strengths")}>
          <MiniMarkdown source={competitor.strengths} className="text-sm" />
        </Section>
      ) : null}
      {competitor.weaknesses ? (
        <Section label={translate("market.competitors.weaknesses")}>
          <MiniMarkdown source={competitor.weaknesses} className="text-sm" />
        </Section>
      ) : null}
      {competitor.notes ? (
        <Section label={translate("market.competitors.notes")}>
          <MiniMarkdown source={competitor.notes} className="text-sm" />
        </Section>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <CalendarCheck aria-hidden className="size-4" />
          {competitor.last_reviewed_at
            ? translate("market.competitors.last_reviewed", {
                date: formatShortDate(competitor.last_reviewed_at, locale),
              })
            : translate("market.competitors.never_reviewed")}
        </span>
        {competitor.last_reviewed_at !== today ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={markReviewed}
            disabled={isPending}
            className="h-8 rounded-full px-3 text-xs font-semibold text-primary"
          >
            {translate("market.competitors.mark_reviewed")}
          </Button>
        ) : null}
      </div>
    </article>
  );
};

const Section = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="min-w-0">
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);
